import { createTelemetryEvent } from "../telemetry/Telemetry";
import { telemetryChannelName } from "./constants";
import { getLanguage, type Language } from "./languageService";

interface CaptionLanguage {
  display: string;
  value: string;
}

function formatDisplayName(language: Language): string {
  if (language.code === "en") {
    return language.display;
  }
  return `${language.display} (${language.native})`;
}

export function formatTelemetryActionMetaValue(languageCode: string): string {
  if (languageCode === "off") {
    return "off";
  }

  const language = getLanguage(languageCode);
  if (!language) {
    return languageCode;
  }

  // For English, return just the language name in lowercase
  if (language.code === "en") {
    return language.display.toLowerCase();
  }

  // For other languages, return "language (code)" in lowercase
  return `${language.display.toLowerCase()} (${language.code})`;
}

export function sendClosedCaptionTelemetryEvent(languageCode: string): void {
  const telemetryValue = formatTelemetryActionMetaValue(languageCode);

  createTelemetryEvent({
    feature: "playback",
    action: "set caption language",
    action_meta: telemetryValue,
  }).fire(telemetryChannelName);
}

export function getCaptionLanguages(
  availableLanguages: {
    language: string;
    path?: string;
  }[],
): CaptionLanguage[] {
  const defaultLanguages = [{ display: "Off", value: "off" }];

  // Dynamically get language info for each available language
  const mappedLanguages = availableLanguages
    .map(value => {
      const language = getLanguage(value.language);
      return language
        ? {
            display: formatDisplayName(language),
            value: value.language,
          }
        : null;
    })
    .filter((lang): lang is CaptionLanguage => lang !== null);

  return [...defaultLanguages, ...mappedLanguages];
}
